import { requireById } from './dom'

interface Tour {
  name: string
  price: number
  duration: string
}

interface Departure {
  day: string
  date: string
  seats: number
}

const TOURS: readonly Tour[] = [
  { name: 'Morning Whale Cruise', price: 95, duration: '3 hrs' },
  { name: 'Coastal Discovery Tour', price: 75, duration: '2 hrs' },
  { name: 'Private Sunset Charter', price: 220, duration: '2.5 hrs' },
]

const DEPARTURES: Departure[] = [
  { day: 'Tue', date: '11 Aug', seats: 14 },
  { day: 'Wed', date: '12 Aug', seats: 3 },
  { day: 'Thu', date: '13 Aug', seats: 9 },
]

const MAX_GUESTS = 10
const LOW_SEATS = 4

/**
 * The demo booking flow: pick a tour and a departure, choose a party size, and
 * "book". Nothing leaves the page — seat counts live in memory for the session.
 */
export function initBookingWidget(): void {
  const tourList = requireById('tours')
  const dateList = requireById('dates')
  const guestCount = requireById('gcount')
  const totalEl = requireById('wtotal')
  const minus = requireById<HTMLButtonElement>('gminus')
  const plus = requireById<HTMLButtonElement>('gplus')
  const bookButton = requireById<HTMLButtonElement>('wbook')
  const form = requireById('wform')
  const confirmation = requireById('wconfirm')
  const confirmMessage = requireById('confmsg')

  let selectedTour = 0
  let selectedDeparture = 0
  let guests = 2

  const tour = (): Tour => TOURS[selectedTour]!
  const departure = (): Departure => DEPARTURES[selectedDeparture]!
  const seatLimit = (): number => Math.min(MAX_GUESTS, departure().seats)

  function renderTours(): void {
    tourList.replaceChildren(
      ...TOURS.map((t, i) => {
        const button = document.createElement('button')
        button.type = 'button'
        button.className = 'tour-opt'
        button.setAttribute('aria-pressed', String(i === selectedTour))
        button.innerHTML =
          `<span>${t.name} <span class="dur">· ${t.duration}</span></span>` +
          `<span class="price">$${t.price}</span>`
        button.addEventListener('click', () => {
          selectedTour = i
          renderTours()
          renderTotal()
        })
        return button
      }),
    )
  }

  function renderDepartures(): void {
    dateList.replaceChildren(
      ...DEPARTURES.map((d, i) => {
        const button = document.createElement('button')
        button.type = 'button'
        button.className = 'date-opt'
        button.setAttribute('aria-pressed', String(i === selectedDeparture))
        const seats = d.seats === 0 ? 'Sold out' : `${d.seats} seats left`
        button.innerHTML =
          `<div class="d1">${d.day}</div><div class="d2">${d.date}</div>` +
          `<span class="seats${d.seats <= LOW_SEATS ? ' low' : ''}">${seats}</span>`
        button.addEventListener('click', () => {
          selectedDeparture = i
          // A quieter departure may not seat the party already chosen.
          guests = Math.max(1, Math.min(guests, seatLimit()))
          renderDepartures()
          renderTotal()
        })
        return button
      }),
    )
  }

  function renderTotal(): void {
    const soldOut = departure().seats === 0
    guestCount.textContent = String(guests)
    totalEl.textContent = `$${soldOut ? 0 : tour().price * guests}`
    minus.disabled = soldOut || guests <= 1
    plus.disabled = soldOut || guests >= seatLimit()
    bookButton.disabled = soldOut
    bookButton.textContent = soldOut ? 'Sold out' : 'Book now'
  }

  minus.addEventListener('click', () => {
    if (guests > 1) {
      guests -= 1
      renderTotal()
    }
  })

  plus.addEventListener('click', () => {
    if (guests < seatLimit()) {
      guests += 1
      renderTotal()
    }
  })

  bookButton.addEventListener('click', () => {
    const { day, date } = departure()
    confirmMessage.textContent =
      `${guests} guest${guests > 1 ? 's' : ''} · ${tour().name} · ${day} ${date}`
    departure().seats = Math.max(0, departure().seats - guests)
    form.style.display = 'none'
    confirmation.classList.add('show')
  })

  requireById('wagain').addEventListener('click', () => {
    confirmation.classList.remove('show')
    form.style.display = 'block'
    guests = Math.max(1, Math.min(guests, seatLimit()))
    renderDepartures()
    renderTotal()
  })

  renderTours()
  renderDepartures()
  renderTotal()
}
